import React, { useContext, useEffect, useState } from "react";
import PageTitle from "../../components/pageTitle/PageTitle";
import { Container } from "@material-ui/core";
import {
  Smartphone,
  Monitor,
  User,
  Building,
  Calendar,
  Clock,
  FileText,
  ChevronLeft,
  Archive,
  X,
} from "lucide-react";
import { useMediaQuery } from "@mui/material";
import AuthContext from "../../hooks/authContext";
import { useNavigate, useParams } from "react-router";
import {
  getSupportTicketDetails,
  unArchiveSupportTicket,
} from "../../services/apiservices/support";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import moment from "moment";
import ViewAttachments from "../IssueManagement/IssueDetails/ViewAttachments";
import {
  getAllEmployee,
} from "../../services/apiservices/employee";
import CommonContext from "../../hooks/commonContext";
import { CommonConfirmationDialog } from "../../components/DialogComponent/SupplyDataDialog";
import { Button } from "../../components/ui/button";

const ArchiveSupportDetails = () => {
  const commonContext = useContext(CommonContext);
  const isMobile = useMediaQuery("(max-width:767px)");
  const navigate = useNavigate();
  const { supportID } = useParams();
  const [employee, setEmployee] = useState();
  const [supports, setSupports] = useState();
  const [confirmingUnArchiveAction, setConfirmingUnArchiveAction] =
    useState(false);
  const auth = useContext(AuthContext);
  const companyId =
    auth?.userProfile?.role === "companyUser"
      ? auth?.userProfile?.id
      : auth?.userProfile?.companyId;

  const fetchData = async () => {
    try {
      commonContext?.setLoader(true);

      const [employeeRes, ticketRes] = await Promise.all([
        new Promise((resolve, reject) => getAllEmployee(resolve, reject)),
        new Promise((resolve) => getSupportTicketDetails(supportID, resolve)),
      ]);

      const employeeList = employeeRes?.data || [];
      const ticket = ticketRes?.data;

      const employeeMap = new Map(
        employeeList.map((emp) => [
          emp?.id,
          {
            name: `${emp?.firstName} ${emp?.lastName}`,
            email: emp?.email || "",
            role: emp?.role,
          },
        ])
      );
      const responses = Object.values(ticket?.responses || {});
      const latestResponse = responses.reduce(
        (a, b) => (a?.respondOn > b?.respondOn ? a : b),
        {}
      );

      const creator = employeeMap.get(ticket?.createdBy);
      const responder = employeeMap.get(latestResponse?.respondBy);

      const companyInfo = employeeList?.find(
        (item) => item?.id === ticket?.companyId
      );
      const companyPlan =
        companyInfo?.subscriptions?.data?.length > 0
          ? companyInfo.subscriptions.data[0]?.plan?.name
          : "Trial Plan";

      const enriched = {
        ...ticket,
        user: creator?.name || "Unknown",
        userEmail: creator?.email || "Unknown",
        respondedByName:
          responses?.length > 0 ? responder?.name || "Unknown" : "No Response",
        company: companyPlan,
        role: responder?.role,
      };

      if (ticketRes?.status) {
        setEmployee(employeeList);
        setSupports(enriched);
      } else {
        console.error("Error fetching ticket:", ticketRes?.error);
        setSupports(undefined);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setSupports(undefined);
    } finally {
      commonContext?.setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [supportID]);

  const handleConfirmUnArchive = async () => {
    const res = await unArchiveSupportTicket(supportID);
    navigate("/help/archiveSupport");
  };

  const handleRedirectToBack = () => {
    navigate("/help/archiveSupport");
  };

  const handleUnArchiveTicketDialogClose = () => {
    setConfirmingUnArchiveAction(false);
  };

  const TicketStatusBadge = ({ status }) => {
    const getVariant = () => {
      switch (status) {
        case "open":
          return { variant: "default", text: "Open" };
        case "pending":
          return { variant: "warning", text: "Pending" };
        case "resolved":
          return { variant: "success", text: "Resolved" };
        case "close":
          return { variant: "outline", text: "Closed" };
        default:
          return { variant: "default", text: status };
      }
    };

    const { variant, text } = getVariant();

    return (
      <Badge
        variant={variant}
        className="min-w-[80px] text-center justify-center"
      >
        {text}
      </Badge>
    );
  };

  const isSuperUser = Array.isArray(auth?.userProfile?.role) ? auth?.userProfile.role.includes("superUser") : auth?.userProfile?.role === "superUser";

  return (
    <>
      <Container>
        <PageTitle
          pageTitle={`Ticket : ${supports?.subject}`}
          showBreadcrumb={
            isSuperUser ? [{ label: "Home", to: "/superuserdashboard" }, { label: "Support", to: "/help/archiveSupport" },] : [{ label: "Home", to: "/" }, { label: "Support", to: "/help/archiveSupport" },]
          }
          BreadCrumbCurrentPageTitle={`Ticket T-${supports?.ticketNumber}`}
          showLastSeen={true}
        />
        <div className={isMobile ? "pv-16 ph-6" : "pv-24 ph-32"}>
          <div className={isMobile ? "mb-24" : "mb-16"}>
            <Button
              variant="outline"
              size="sm"
              className="flex items-center"
              style={{
                fontWeight: "500",
                fontSize: "14px",
                border: "1px solid hsl(214.3, 31.8%, 91.4%)",
                padding: "0px 12px",
                width: isMobile ? "410px" : "150px",
                height: "36px",
              }}
              onClick={handleRedirectToBack}
            >
              <ChevronLeft className="h-4 w-4" />
              Back to Tickets
            </Button>
          </div>
          <div className="d-flex flex-center">
            <div>
              <Button
                variant="outline"
                onClick={() => setConfirmingUnArchiveAction(true)}
                style={{
                  border: "1px solid hsl(214.3, 31.8%, 91.4%)",
                  borderRadius: "4px",
                  fontWeight: "500",
                  fontSize: "14px",
                  width: isMobile ? "410px" : "",
                }}
              >
                <Archive className="h-4 w-4 mr-4" /> UnArchive Ticket
              </Button>
            </div>
          </div>
          <div
            className={isMobile ? "sectionBox pv-15 ph-10 dark:bg-[#2c3440]" : "sectionBox p-24 dark:bg-[#2c3440]"}
            style={{
              border: "1px solid hsl(214.3, 31.8%, 91.4%)",
              borderRadius: "8px",
            }}
          >
            <Card>
              <CardContent className="p-16">
                <div className="mb-12">
                  <h1 className="text-xl font-bold">{supports?.subject}</h1>
                  <div className="flex flex-wrap items-center gap-2 mt-4">
                    <TicketStatusBadge status={supports?.status} />
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-800">
                      {supports?.portal === "web" ? "Portal" : "Mobile"}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">
                      {supports?.type === "technical" ? "Technical" : "General"}
                    </span>
                    <div className="flex items-center text-xs text-gray-500">
                      {supports?.portal === "Mobile App" ? (
                        <Smartphone className="h-3 w-3 mr-4" />
                      ) : (
                        <Monitor className="h-3 w-3 mr-4" />
                      )}
                      <span>
                        {supports?.portal === "web" ? "Web App" : "Mobile App"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <User className="h-4 w-4 mr-8 text-gray-500" />
                      <span>
                        <strong>From:</strong> {supports?.user} (
                        {supports?.userEmail})
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Building className="h-4 w-4 mr-8 text-gray-500" />
                      <span>
                        <strong>Account Type:</strong> {supports?.company}
                      </span>
                    </div>
                    {supports?.respondedByName && (
                      <div className="flex items-center text-sm text-gray-600">
                        <User className="h-4 w-4 mr-8 text-gray-500" />
                        <span>
                          <strong>Last Responded By:</strong>
                          <span
                            className={`ml-4`}
                            style={{
                              color:
                                supports?.role === "superUser" ? "#2563EB" : "",
                              fontWeight:
                                supports?.role === "superUser" ? "500" : "",
                              fontSize:
                                supports?.role === "superUser" ? "14px" : "",
                            }}
                          >
                            {supports?.respondedByName}
                          </span>
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="h-4 w-4 mr-8 text-gray-500" />
                      <span>
                        <strong>Created:</strong>{" "}
                        {moment
                          .unix(supports?.createdAt.seconds)
                          .format("MMM D, YYYY h:mm A")}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Clock className="h-4 w-4 mr-8 text-gray-500" />
                      <span>
                        <strong>Last Updated:</strong>{" "}
                        {moment
                          .unix(supports?.updatedAt.seconds)
                          .format("MMM D, YYYY h:mm A")}
                      </span>
                    </div>
                    {supports?.attachments &&
                      supports?.attachments?.length > 0 && (
                        <div className="flex items-center text-sm text-gray-600">
                          <FileText className="h-4 w-4 mr-8 text-gray-500" />
                          <span>
                            <strong>Attachments:</strong>{" "}
                            {supports?.attachments?.length}
                          </span>
                        </div>
                      )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="mt-32">
              <CardHeader className="p-24">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-base font-medium flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {supports?.user}
                    </CardTitle>
                    <p className="text-sm text-gray-500">
                      {moment
                        .unix(supports?.createdAt.seconds)
                        .format("MMM D, YYYY h:mm A")}
                    </p>
                  </div>
                  {supports?.portal && (
                    <div className="text-xs px-3 py-1 rounded-full bg-blue-100 text-blue-800">
                      {supports?.portal === "web" ? "Portal" : "Mobile"}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-0 pb-24 ph-24">
                <p
                  style={{ fontSize: "16px" }}
                  className="whitespace-pre-line"
                >
                  {supports?.description}
                </p>

                {supports?.attachments && supports?.attachments.length > 0 && (
                  <div className="mt-16">
                    <p className="text-sm font-medium text-gray-700 mb-8">
                      Attachments:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <div className="flex items-center rounded-md bg-slate-100 p-8 text-sm">
                        <FileText className="h-4 w-4 mr-8 text-slate-500" />
                        <ViewAttachments attachments={supports?.attachments} />
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-16 text-sm text-gray-500">
                  <p className="flex items-center gap-1.5">
                    Last Update:{" "}
                    {moment
                      .unix(supports?.updatedAt.seconds)
                      .format("MMM D, YYYY h:mm A")}
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="mt-32">
              <h2
                className="mb-16 text-foreground"
                style={{
                  fontSize: "18px",
                  fontWeight: "500",
                }}
              >
                Comment History
              </h2>
              {supports?.responses &&
                Object.values(supports.responses)
                  .sort((a, b) => b.respondOn - a.respondOn)
                  .map((responseItem, index) => {
                    const data = employee?.find(
                      (item) => item?.id === responseItem?.respondBy
                    );

                    return (
                      <Card
                        key={index}
                        className="mt-32"
                        style={{
                          border: "1px solid hsl(214.3, 31.8%, 91.4%)",
                          borderRadius: "8px",
                          boxShadow: "none",
                        }}
                      >
                        <CardContent className="ph-24 pv-24">
                          <div className="d-flex flex-space-between">
                            <div>
                              <div
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "500",
                                }}
                              >
                                {responseItem?.respondBy?.name}
                              </div>
                              <p
                                className="mt-8 mb-0"
                                style={{ color: "#6B7280" }}
                              >
                                {moment(responseItem?.respondOn).format(
                                  "MMM D, YYYY h:mm A"
                                )}
                              </p>
                            </div>

                            {responseItem?.attachments?.length > 0 && (
                              <div style={{ width: isMobile ? "40%" : "" }}>
                                <ViewAttachments
                                  attachments={responseItem?.attachments}
                                />
                              </div>
                            )}
                          </div>
                        </CardContent>
                        <div
                          className="ph-24 pt-0 pb-24"
                          style={{ fontSize: "16px" }}
                        >
                          {responseItem?.response}
                        </div>
                      </Card>
                    );
                  })}
            </div>
          </div>
        </div>
      </Container>
      {confirmingUnArchiveAction && (
        <CommonConfirmationDialog
          open={confirmingUnArchiveAction}
          onConfirm={handleConfirmUnArchive}
          onClose={handleUnArchiveTicketDialogClose}
          title={"UnArchive Ticket"}
          message="Are you sure you want to unArchive this ticket?"
          buttonTitle={"Yes, unArchive ticket"}
        />
      )}
    </>
  );
};

export default ArchiveSupportDetails;
